import React, { useState, useRef, useEffect } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import Spinner from "react-bootstrap/Spinner";
import { FaTimes } from "react-icons/fa";

const CATEGORIES = [
  { value: "feature_request", label: "Feature Request", emoji: "💡" },
  { value: "bug_report", label: "Bug Report", emoji: "🐛" },
  { value: "improvement", label: "Improvement", emoji: "✨" },
  { value: "feedback", label: "General Feedback", emoji: "💬" },
];

const SIDEBAR_WIDTH = 260;

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("feature_request");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const textareaRef = useRef(null);

  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    if (open && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [open]);

  // Allow external triggers (e.g. sidebar) to open the widget
  useEffect(() => {
    function handler() { reset(); setOpen(true); }
    window.addEventListener('open-feedback-widget', handler);
    return () => window.removeEventListener('open-feedback-widget', handler);
  }, []);

  function reset() {
    setCategory("feature_request");
    setMessage("");
    setSubmitted(false);
  }

  async function handleSubmit() {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/submit-feedback`,
        { category, message: message.trim(), page_url: window.location.href }
      );
      setSubmitted(true);
    } catch {
      alert("Failed to submit feedback. Please try again.");
    }
    setSubmitting(false);
  }

  return (
    <>
      {/* Modal overlay */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed", top: 0, left: isDesktop ? SIDEBAR_WIDTH : 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.3)", zIndex: 2000,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 16, width: 380, maxWidth: "calc(100vw - 48px)",
              boxShadow: "0 12px 48px rgba(0,0,0,0.18)",
              display: "flex", flexDirection: "column", overflow: "hidden",
            }}
          >
            {/* Header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "16px 20px 12px", borderBottom: "1px solid #f3f4f6",
            }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: "#1a1a1a" }}>
                Send Feedback
              </span>
              <button
                onClick={() => setOpen(false)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#9ca3af", padding: 4 }}
              ><FaTimes /></button>
            </div>

            {/* Body */}
            <div style={{ padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
              {submitted ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: "#1a1a1a", marginBottom: 4 }}>
                    Thanks for your feedback!
                  </div>
                  <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
                    We'll review it and get back to you if needed.
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    style={{
                      background: "#7c3aed", color: "#fff", border: "none",
                      borderRadius: 8, padding: "8px 24px", fontWeight: 600,
                      cursor: "pointer", fontSize: 13,
                    }}
                  >Close</button>
                </div>
              ) : (
                <>
                  {/* Category pills */}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                      Category
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {CATEGORIES.map(c => (
                        <button
                          key={c.value}
                          onClick={() => setCategory(c.value)}
                          style={{
                            background: category === c.value ? "#f5f3ff" : "#f3f4f6",
                            color: category === c.value ? "#6d28d9" : "#374151",
                            border: category === c.value ? "1px solid #c4b5fd" : "1px solid #e5e7eb",
                            borderRadius: 999, padding: "5px 12px", fontSize: 12,
                            fontWeight: 600, cursor: "pointer", display: "flex",
                            alignItems: "center", gap: 4, transition: "all 0.15s",
                          }}
                        >
                          <span style={{ fontSize: 13 }}>{c.emoji}</span> {c.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                      Message
                    </div>
                    <textarea
                      ref={textareaRef}
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      placeholder={
                        category === "bug_report"
                          ? "Describe the bug — what happened, what you expected, and steps to reproduce…"
                          : category === "feature_request"
                          ? "Describe the feature you'd like to see…"
                          : "Share your thoughts…"
                      }
                      rows={4}
                      style={{
                        width: "100%", padding: "10px 12px", borderRadius: 8,
                        border: "1px solid #e5e7eb", fontSize: 13, boxSizing: "border-box",
                        resize: "vertical", fontFamily: "inherit", lineHeight: 1.5,
                      }}
                    />
                  </div>

                  {/* Submit */}
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || !message.trim()}
                    style={{
                      background: submitting || !message.trim() ? "#a78bfa" : "#7c3aed",
                      color: "#fff", border: "none", borderRadius: 8,
                      padding: "10px 20px", fontWeight: 600,
                      cursor: submitting || !message.trim() ? "not-allowed" : "pointer",
                      fontSize: 14, display: "flex", alignItems: "center",
                      justifyContent: "center", gap: 8, width: "100%",
                    }}
                  >
                    {submitting && <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />}
                    {submitting ? "Submitting…" : "Submit Feedback"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
